import React, { useState, useEffect, useCallback } from 'react';
import {
  Zap, Clock, MapPin, Calendar, User, Loader2, Plus, X, AlertTriangle,
  Sparkles, CheckCircle2, Briefcase, RefreshCw, TrendingUp, Coins,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Overtime Marketplace — managers post open overtime slots; staff pick them up.
 * Picked-up slots automatically drop onto the staff member's calendar as a shift.
 */
const OvertimeMarketplace = ({ getAuthHeaders, currentUser }) => {
  const isManager = ['manager', 'admin', 'executive', 'superadmin'].includes(currentUser?.role);
  const [openSlots, setOpenSlots] = useState([]);
  const [mySlots, setMySlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [showPost, setShowPost] = useState(false);
  const [tab, setTab] = useState('open');
  const [form, setForm] = useState({
    date: '', start_time: '18:00', end_time: '22:00', role: '', location: '',
    notes: '', pay_rate_multiplier: 1.5, slots: 1,
  });
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [openR, mineR] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/shifts/overtime/open`, { headers: getAuthHeaders() }),
        axios.get(`${BACKEND_URL}/api/shifts/overtime/mine`, { headers: getAuthHeaders() }),
      ]);
      setOpenSlots(openR.data.slots || []);
      setMySlots(mineR.data.slots || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load overtime');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const pickup = async (slot) => {
    setBusy(slot.id);
    try {
      await axios.post(`${BACKEND_URL}/api/shifts/overtime/${slot.id}/pickup`, {}, { headers: getAuthHeaders() });
      toast.success('Overtime grabbed — it\'s on your calendar.', {
        description: `${slot.date} · ${slot.start_time}–${slot.end_time}`,
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not claim slot');
    } finally {
      setBusy(null);
    }
  };

  const cancelSlot = async (slot) => {
    if (!window.confirm(`Withdraw this overtime slot? ${slot.claimed_by ? 'It\'ll be removed from ' + slot.claimed_by_name + '\'s calendar.' : ''}`)) return;
    setBusy(slot.id);
    try {
      await axios.delete(`${BACKEND_URL}/api/shifts/overtime/${slot.id}`, { headers: getAuthHeaders() });
      toast.success('Overtime withdrawn');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally {
      setBusy(null);
    }
  };

  const submitPost = async () => {
    if (!form.date || !form.start_time || !form.end_time) {
      return toast.error('Date and times are required');
    }
    setPosting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/shifts/overtime/post`, form, { headers: getAuthHeaders() });
      toast.success(`Posted ${form.slots} slot${form.slots === 1 ? '' : 's'}`);
      setShowPost(false);
      setForm({ date: '', start_time: '18:00', end_time: '22:00', role: '', location: '', notes: '', pay_rate_multiplier: 1.5, slots: 1 });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="overtime-marketplace">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-gradient-to-br from-[#0A0A14] to-[#0F0A1F] p-6">
        <div className="pointer-events-none absolute -top-16 right-0 w-[380px] h-[220px] bg-[#00E5FF]/[0.07] rounded-full blur-[120px]" />
        <div className="pointer-events-none absolute -bottom-16 left-0 w-[320px] h-[200px] bg-[#8A2BE2]/[0.08] rounded-full blur-[110px]" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#00E5FF]/[0.15] to-[#8A2BE2]/[0.15] border border-white/10 flex items-center justify-center flex-shrink-0"
              style={{ boxShadow: '0 0 30px rgba(0,229,255,0.15)' }}>
              <Zap className="w-5 h-5 text-[#00E5FF]" />
            </div>
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase text-white/40 font-medium">Marketplace</p>
              <h2 className="text-2xl font-light text-white tracking-tighter">
                Overtime <span className="bg-gradient-to-r from-[#00E5FF] to-[#c39be5] bg-clip-text text-transparent font-medium">Board</span>
              </h2>
              <p className="text-white/40 text-xs mt-1 max-w-md">Managers post extra hours, staff grab them first come, first served — instantly on your calendar.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={load} variant="ghost" size="sm" className="h-9 w-9 p-0 rounded-full text-white/40 hover:text-white" data-testid="refresh-overtime">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            {isManager && (
              <Button onClick={() => setShowPost(true)} data-testid="post-overtime-btn"
                className="bg-gradient-to-br from-[#00E5FF] to-[#0099CC] text-black hover:opacity-90 rounded-full px-4 h-9 text-[10px] tracking-[0.12em] uppercase font-semibold">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Post overtime
              </Button>
            )}
          </div>
        </div>

        {/* Stats strip */}
        <div className="relative mt-6 grid grid-cols-3 gap-3">
          <MiniStat icon={Sparkles} label="Open now" value={openSlots.length} tone="cyan" />
          <MiniStat icon={CheckCircle2} label="You've claimed" value={mySlots.length} tone="emerald" />
          <MiniStat icon={Coins} label="Extra hours" value={mySlots.reduce((s, x) => s + (x.hours || 0), 0).toFixed(1)} tone="violet" hint="hrs added this period" />
        </div>
      </div>

      {/* Tab pills */}
      <div className="flex items-center gap-1">
        {[
          { id: 'open', label: `Open (${openSlots.length})` },
          { id: 'mine', label: `Claimed by me (${mySlots.length})` },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`overtime-tab-${t.id}`}
            className={`px-3.5 py-1.5 rounded-full text-[10px] tracking-[0.12em] uppercase font-medium transition-all ${
              tab === t.id
                ? 'bg-[#00E5FF]/[0.10] text-[#00E5FF] border border-[#00E5FF]/25'
                : 'bg-white/[0.02] text-white/50 border border-white/[0.05] hover:text-white/80'
            }`}>{t.label}</button>
        ))}
      </div>

      {/* Slots grid */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-white/40 animate-spin" /></div>
      ) : (tab === 'open' ? openSlots : mySlots).length === 0 ? (
        <div className="rounded-2xl border border-white/[0.06] bg-[#0A0A12] p-12 text-center">
          <Zap className="w-10 h-10 text-white/15 mx-auto mb-3" />
          <p className="text-white/40 text-sm">{tab === 'open' ? 'No open overtime right now. Check back later!' : 'You haven\'t claimed any overtime yet.'}</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {(tab === 'open' ? openSlots : mySlots).map((s) => (
            <OvertimeCard key={s.id} slot={s} tab={tab} busy={busy === s.id}
              isManager={isManager} onPickup={pickup} onCancel={cancelSlot} />
          ))}
        </div>
      )}

      {/* Post overtime modal (managers) */}
      <Dialog open={showPost} onOpenChange={(v) => !v && setShowPost(false)}>
        <DialogContent className="bg-[#0A0A14] border border-white/[0.08] text-white max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-white text-base font-medium">Post overtime</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Field label="Date">
              <input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} data-testid="ot-date"
                className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00E5FF]/40" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Start"><input type="time" value={form.start_time} onChange={(e) => setForm({...form, start_time: e.target.value})} className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00E5FF]/40" data-testid="ot-start" /></Field>
              <Field label="End"><input type="time" value={form.end_time} onChange={(e) => setForm({...form, end_time: e.target.value})} className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00E5FF]/40" data-testid="ot-end" /></Field>
            </div>
            <Field label="Role (optional)"><input value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} placeholder="e.g. Front desk" className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00E5FF]/40" /></Field>
            <Field label="Location (optional)"><input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} placeholder="e.g. Main branch" className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00E5FF]/40" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Pay multiplier"><input type="number" step="0.1" min="1" value={form.pay_rate_multiplier} onChange={(e) => setForm({...form, pay_rate_multiplier: parseFloat(e.target.value) || 1.5})} className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00E5FF]/40" /></Field>
              <Field label="Number of slots"><input type="number" min="1" max="10" value={form.slots} onChange={(e) => setForm({...form, slots: parseInt(e.target.value) || 1})} className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00E5FF]/40" data-testid="ot-slots" /></Field>
            </div>
            <Field label="Notes (optional)"><textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value.slice(0, 500)})} rows={2} placeholder="Anything staff should know" className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-3 py-2 text-white text-xs resize-none focus:outline-none focus:border-[#00E5FF]/40" /></Field>
            <div className="flex items-center gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowPost(false)} className="text-white/50 hover:text-white rounded-xl">Cancel</Button>
              <Button onClick={submitPost} disabled={posting} data-testid="ot-submit-btn"
                className="flex-1 bg-gradient-to-br from-[#00E5FF] to-[#0099CC] text-black hover:opacity-90 rounded-xl font-medium">
                {posting ? 'Posting…' : 'Post overtime'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const MiniStat = ({ icon: Icon, label, value, tone, hint }) => {
  const tones = {
    cyan: 'from-[#00E5FF]/[0.08] border-[#00E5FF]/20 text-[#00E5FF]',
    emerald: 'from-emerald-500/[0.08] border-emerald-500/20 text-emerald-300',
    violet: 'from-[#8A2BE2]/[0.10] border-[#8A2BE2]/25 text-[#c39be5]',
  };
  return (
    <div className={`relative rounded-xl border bg-gradient-to-br to-transparent px-4 py-3 ${tones[tone]}`}>
      <div className="flex items-start justify-between">
        <p className="text-[9px] tracking-[0.15em] uppercase text-white/40 font-medium">{label}</p>
        <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
      </div>
      <p className="text-white text-2xl font-light tracking-tight tabular-nums mt-1">{value}</p>
      {hint && <p className="text-white/25 text-[10px] mt-1">{hint}</p>}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] tracking-[0.15em] uppercase text-white/40 font-medium mb-1.5">{label}</label>
    {children}
  </div>
);

const OvertimeCard = ({ slot, tab, busy, isManager, onPickup, onCancel }) => {
  const d = slot.date ? new Date(slot.date + 'T00:00:00') : null;
  const day = d?.toLocaleDateString('en-GB', { day: '2-digit' }) || '--';
  const month = d?.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase() || '';
  const weekday = d?.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase() || '';
  const isMine = tab === 'mine';
  return (
    <div className={`rounded-2xl border p-5 backdrop-blur-md transition-all hover:border-white/[0.15] group ${
      isMine ? 'bg-emerald-500/[0.03] border-emerald-500/20' : 'bg-white/[0.02] border-white/[0.06]'
    }`} data-testid={`overtime-slot-${slot.id}`}>
      <div className="flex items-start gap-4">
        {/* Date tile */}
        <div className={`w-16 h-16 rounded-2xl flex flex-col items-center justify-center flex-shrink-0 border ${
          isMine
            ? 'bg-gradient-to-br from-emerald-500/[0.15] to-transparent border-emerald-500/30'
            : 'bg-gradient-to-br from-[#00E5FF]/[0.12] to-transparent border-[#00E5FF]/25'
        }`}>
          <span className={`text-[9px] tracking-[0.15em] font-semibold ${isMine ? 'text-emerald-300' : 'text-[#00E5FF]'}`}>{weekday}</span>
          <span className="text-white text-xl font-light leading-none tabular-nums">{day}</span>
          <span className="text-white/40 text-[9px] tracking-[0.15em] mt-0.5">{month}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-white/40" />
              <span className="text-white text-sm font-medium tabular-nums">{slot.start_time}–{slot.end_time}</span>
              <span className="text-white/40 text-xs">· {slot.hours}h</span>
            </div>
            {slot.pay_rate_multiplier && slot.pay_rate_multiplier > 1 && (
              <span className="px-1.5 py-0.5 rounded-md text-[9px] tracking-[0.1em] uppercase font-semibold bg-[#00E5FF]/[0.08] text-[#00E5FF] border border-[#00E5FF]/20 tabular-nums">
                x{slot.pay_rate_multiplier}
              </span>
            )}
          </div>
          {slot.role && <p className="text-white/70 text-xs mb-1"><Briefcase className="w-3 h-3 inline mr-1 text-white/30" /> {slot.role}</p>}
          {slot.location && <p className="text-white/50 text-xs mb-1"><MapPin className="w-3 h-3 inline mr-1 text-white/30" /> {slot.location}</p>}
          {slot.notes && <p className="text-white/50 text-[11px] mt-2 leading-relaxed">{slot.notes}</p>}
          <p className="text-white/25 text-[10px] mt-2">
            Posted by <span className="text-white/50">{slot.posted_by_name}</span>
            {isMine && slot.claimed_at && <span> · Claimed {new Date(slot.claimed_at).toLocaleDateString([], { day: 'numeric', month: 'short' })}</span>}
          </p>
        </div>
      </div>
      {!isMine && (
        <div className="mt-4 flex items-center gap-2">
          <Button onClick={() => onPickup(slot)} disabled={busy} data-testid={`pickup-btn-${slot.id}`}
            className="flex-1 bg-gradient-to-br from-[#00E5FF] to-[#0099CC] text-black hover:opacity-90 rounded-full font-semibold text-[10px] tracking-[0.12em] uppercase h-9 disabled:opacity-40">
            {busy ? 'Grabbing…' : <><Zap className="w-3.5 h-3.5 mr-1.5" /> Grab this shift</>}
          </Button>
          {isManager && (
            <Button onClick={() => onCancel(slot)} disabled={busy} variant="ghost"
              className="h-9 w-9 p-0 rounded-full text-white/40 hover:text-red-300">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}
      {isMine && (
        <div className="mt-3 flex items-center gap-2 pt-3 border-t border-emerald-500/10">
          <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.1em] uppercase text-emerald-300 font-medium">
            <CheckCircle2 className="w-3 h-3" /> On your calendar
          </span>
          <span className="ml-auto text-white/30 text-[10px]">Contact your manager if you need to drop it.</span>
        </div>
      )}
    </div>
  );
};

export default OvertimeMarketplace;
